import { NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LayoutDashboard, CalendarDays, Users, UserCircle, Settings as SettingsIcon, LogOut, Shield } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import CatMascot from "./CatMascot";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/events", label: "Events", icon: CalendarDays, testid: "nav-events" },
  { to: "/staff", label: "Staff", icon: UserCircle, testid: "nav-staff" },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const name = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="flex min-h-screen bg-[#0A0C10] text-slate-100">
      <aside className="fixed left-0 top-0 z-40 flex h-screen w-[260px] flex-col border-r border-slate-800/80 bg-[#0C0F16]">
        <div className="flex items-center gap-3 px-6 py-6">
          <CatMascot size={40} />
          <div>
            <p className="font-display text-lg font-black leading-none text-white">GG's Kingdom</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-purple-400">Staff System</p>
          </div>
        </div>

        <nav className="mt-2 flex-1 space-y-1 px-4">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testid}
              className={({ isActive }) =>
                `group relative flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all ${
                  isActive ? "bg-purple-600/15 text-purple-300" : "text-slate-400 hover:bg-slate-800/50 hover:text-slate-200"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && <motion.span layoutId="nav-active" className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r bg-purple-500" />}
                  <n.icon className="h-5 w-5" />
                  {n.label}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-slate-800/80 p-4">
          <div className="flex items-center gap-3 rounded-xl bg-slate-800/40 p-3">
            <Avatar className="h-9 w-9 border border-purple-500/40">
              <AvatarImage src={user?.avatar_url || user?.discord_avatar} />
              <AvatarFallback className="bg-purple-600/30 text-xs text-purple-200">{name.slice(0, 2).toUpperCase() || "GG"}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-white">{name || user?.discord_username}</p>
              <p className="flex items-center gap-1 truncate text-[11px] text-purple-400">
                <Shield className="h-3 w-3" /> {user?.role?.name || "Staff"}
              </p>
            </div>
            <button data-testid="logout-btn" onClick={handleLogout} className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-500/15 hover:text-red-400">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      <main className="ml-[260px] flex-1">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="px-8 py-8">
          {children}
        </motion.div>
      </main>
    </div>
  );
}
